/**
 * Shared formatting helpers for the /data (ETL) ecosystem.
 *
 * Single source of truth — before this file existed, four components carried
 * four divergent date formatters and three copies of the sheet-type maps.
 */

export function formatDateTime(iso) {
  if (!iso) return '—';
  // Date-only strings parse as UTC midnight; anchor them to local midnight so
  // the displayed day doesn't shift in US timezones.
  const value = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso;
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function formatDate(iso) {
  if (!iso) return '—';
  const value = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso;
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatAge(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function formatSize(bytes) {
  if (bytes === null || bytes === undefined || bytes === '') return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function formatCount(n) {
  if (n === null || n === undefined || n === '') return '—';
  return Number(n).toLocaleString();
}

export const SHEET_TYPES = {
  data_sheet: 'Data Sheet',
  metadata: 'Metadata',
  scorecard_dashboard: 'Scorecard',
  pivot_table: 'Pivot Table',
};

export function sheetTypeLabel(type) {
  return SHEET_TYPES[type] || type || '—';
}

export function sheetTypeClass(type) {
  return {
    data_sheet: 'bg-emerald-50 text-emerald-700',
    metadata: 'bg-blue-50 text-blue-700',
    scorecard_dashboard: 'bg-purple-50 text-purple-700',
    pivot_table: 'bg-amber-50 text-amber-700',
  }[type] || 'bg-gray-100 text-gray-600';
}

// Backend emits mixed casing ('SUCCESS' vs 'success') between routine status
// and sheet status — normalize once here instead of duplicating both casings.
export function statusKey(status) {
  return (status || '').toLowerCase();
}

export function statusDotClass(status) {
  return {
    success: 'bg-emerald-500',
    completed: 'bg-emerald-500',
    on_schedule: 'bg-emerald-500',
    partial: 'bg-amber-400',
    overdue: 'bg-amber-400',
    running: 'bg-blue-400',
    pending: 'bg-gray-300',
    failed: 'bg-red-500',
    error: 'bg-red-500',
    stale: 'bg-red-500',
    never_succeeded: 'bg-red-500',
    skipped: 'bg-gray-300',
  }[statusKey(status)] || 'bg-gray-300';
}

export function confidenceClass(confidence) {
  if (confidence >= 0.8) return 'text-emerald-600';
  if (confidence >= 0.5) return 'text-amber-600';
  return 'text-red-600';
}

// Axios error -> message. DRF answers a permission failure with {detail: ...}
// and this module's own views with {error: ...}; reading only `error` turned
// every 403 into a bare "Failed to ..." that read as a broken page rather than
// a missing grant.
export function etlErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (err?.response?.status === 403) {
    return data?.error || data?.detail
      || "You don't have permission to do this. Ask an admin for Data Ingestion access.";
  }
  return data?.error || data?.detail || err?.message || fallback;
}
